import { AuthenticatedSession } from '@/authenticatedSession/authenticatedSession';
import type { MerchantAccount, TransactionDocument } from '@shared/types';
import { isTransactionStatusFailedOrReturned } from '@/utils/transactionStatus';

type AccountDisplayAccount = Pick<MerchantAccount, 'accountNickname' | 'id'>;
type AccountDisplayTransaction = Pick<
  TransactionDocument,
  | 'fromInternalMerchantAccountId'
  | 'internalMerchantAccountId'
  | 'toInternalMerchantAccountId'
  | 'type'
>;
type TransactionAccountDirectionFields = Pick<
  TransactionDocument,
  'fromInternalMerchantAccountId' | 'toInternalMerchantAccountId'
>;

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatDate = (timestamp: Date | undefined): string => {
  return (
    timestamp?.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }) ?? ''
  );
};

export const formatDateTime = (timestamp: Date | undefined): string => {
  return timestamp?.toLocaleString() ?? '';
};

export const getTransactionAccountDisplayValues = ({
  transaction,
  accounts,
}: {
  transaction: AccountDisplayTransaction;
  accounts: AccountDisplayAccount[];
}): { fromAccount: string; toAccount: string } => {
  const { fromAccountId, toAccountId } = getTransactionAccountDisplayIds({
    transaction,
  });

  return {
    fromAccount: getAccountLabel(fromAccountId, accounts),
    toAccount: getAccountLabel(toAccountId, accounts),
  };
};

export const getTransactionAccountDisplayIds = ({
  transaction,
}: {
  transaction: AccountDisplayTransaction;
}): { fromAccountId?: string; toAccountId?: string } => {
  const hasExplicitAccountDirection =
    !!transaction.fromInternalMerchantAccountId ||
    !!transaction.toInternalMerchantAccountId;

  if (hasExplicitAccountDirection) {
    return {
      fromAccountId: transaction.fromInternalMerchantAccountId,
      toAccountId: transaction.toInternalMerchantAccountId,
    };
  }

  if (!transaction.internalMerchantAccountId) return {};

  if (isLegacyFromAccountType(transaction.type)) {
    return { fromAccountId: transaction.internalMerchantAccountId };
  }
  return { toAccountId: transaction.internalMerchantAccountId };
};

export const isLegacyFromAccountType = (
  type: AccountDisplayTransaction['type'] | undefined,
): boolean =>
  type === 'refundFromOperating' ||
  type === 'refundFromTrust' ||
  type === 'dispute';

export const getTransactionAccountDirectionFields = ({
  type,
  accountId,
}: {
  type: AccountDisplayTransaction['type'] | undefined;
  accountId: string | undefined;
}): Partial<TransactionAccountDirectionFields> => {
  if (!accountId) return {};

  if (isLegacyFromAccountType(type)) {
    return { fromInternalMerchantAccountId: accountId };
  }
  if (
    type === 'invoicePayment' ||
    type === 'retainerDeposit' ||
    type === 'retainerTransfer' ||
    type === 'insurance' ||
    type === 'unknown'
  ) {
    return { toInternalMerchantAccountId: accountId };
  }
  return {};
};

const getAccountLabel = (
  accountId: string | undefined,
  accounts: AccountDisplayAccount[],
): string => {
  if (!accountId) return '';
  return (
    accounts.find((account) => account.id === accountId)?.accountNickname ??
    accountId
  );
};

export const isUnviewedAlertTransaction = (
  transaction: TransactionDocument,
  isAdmin: boolean,
  authSession: AuthenticatedSession | undefined,
) => {
  if (!isAdmin || !isTransactionStatusFailedOrReturned(transaction.status)) {
    return false;
  }
  if (!authSession?.userIds.firestore) return false;
  return (
    transaction.failedViewedBy?.includes(authSession.userIds.firestore) !== true
  );
};

export const isUnviewedDraftTransaction = (
  transaction: TransactionDocument,
  isSuperAdmin: boolean,
  authSession: AuthenticatedSession | undefined,
) => {
  if (!isSuperAdmin || transaction.status !== 'draft') return false;
  if (!authSession?.userIds.firestore) return false;
  return (
    transaction.draftViewedBy?.includes(authSession.userIds.firestore) !== true
  );
};
